import type { ColumnItemBranch, ColumnLeaf } from '~/lib/select-column'
import { ColumnItem } from '~/lib/select-column'
import type { ComputedRef, Ref } from 'vue'
import { computed, MaybeRefOrGetter, mergeProps, toValue } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { DataTableSortItem } from 'vuetify/framework'
import { DataTableHeader } from 'vuetify/framework'
import type { NestedLeaf } from '~/lib/nested'
import { getLeafsAll, getParentsLeafs, isNestedBranch, reduceTreeInplace, walkNestedItems } from '~/lib/nested'
import { copy } from '~/helper'
import useI18nGlobal from '~/composables/common/useI18nGlobal'
import { roundToStep } from '~/helper/math'

export const RowNumberColumn: DataTableHeader = {
  key: 'row-number',
  title: '#',
  width: '42px',
  sortable: false,
  align: 'center',
}
export const ActionColumn: DataTableHeader = { key: 'action', title: '', fixed: 'end', width: '42px', sortable: false }
export const ExpandColumn: DataTableHeader = { key: 'data-table-expand' }

export type TableHeader<T extends object> = DataTableHeader<T> & {
  col: ColumnItem<T>
  tooltip: string
}

export type TableHeaderBranch<T extends object> = Omit<TableHeader<T>, 'children' | 'col'> & {
  col: ColumnItemBranch<T>
  children: TableHeader<T>[]
}
export function isHeaderBranch<T extends object>(item: TableHeader<T>): item is TableHeaderBranch<T> {
  return 'children' in item && Array.isArray(item.children)
}

type BaseOptions<TRow extends {}> = {
  columnsTitle: (item: ColumnItem<TRow>) => string
}

type TableColumnsOptions<TRow extends {}> = BaseOptions<TRow> & {
  columnsBase: MaybeRefOrGetter<ColumnItem<TRow>[]>
  columnsDefault?: string[] // ids of columns to be default selected
  storageKey: string
  purgeStorage?: boolean
}
type TableColumnsOptionsBase<TRow extends {}> = BaseOptions<TRow> & {
  columns: Ref<ColumnItem<TRow>[]>
  sort: Ref<DataTableSortItem[]>
}

/**
 * Return type for base table columns
 */
export type UseTabeCols<TRow extends {}> = {
  columns: Ref<ColumnItem<TRow>[]>
  sort: Ref<DataTableSortItem[]>
  headers: ComputedRef<TableHeader<TRow>[]>
  headersAll: ComputedRef<TableHeader<TRow>[]>
  headerLeafs: ComputedRef<TableHeader<TRow>[]>

  columnLeafs: ComputedRef<NestedLeaf<ColumnLeaf<TRow>>[]>
  columnBuckets: ComputedRef<{
    parents: NestedLeaf<ColumnItem<TRow>>[]
    leafs: NestedLeaf<ColumnLeaf<TRow>>[]
  }>

  resizeColumn: <T>(e: MouseEvent, col: ColumnItem<T>) => void
  resizeColumnReset: <T>(col: ColumnItem<T>) => void
}

/**
 * Base table columns management (accepts refs for columns and sort)
 */
export function useTableColumnsBase<TRow extends {}>(options: TableColumnsOptionsBase<TRow>): UseTabeCols<TRow> {
  const columnBuckets = computed(() => getParentsLeafs(options.columns.value))
  const columnLeafs = computed(() => columnBuckets.value.leafs)

  const headers = computed((): TableHeader<TRow>[] =>
    options.columns.value.map((col) => columnToHeader(col, options.columnsTitle)),
  )

  const headersAll = computed(() => {
    const _headers: TableHeader<TRow>[] = []
    walkNestedItems(headers.value, (header) => {
      _headers.push(header)
    })
    return _headers
  })

  const headerLeafs = computed(() => headersAll.value.filter((c) => !isHeaderBranch(c)))

  return {
    columns: options.columns,
    sort: options.sort,
    headers,
    columnLeafs,
    headerLeafs,
    columnBuckets: columnBuckets,
    headersAll: headersAll,
    resizeColumn,
    resizeColumnReset,
  }
}

/**
 * Manage table columns state with localStorage persistence
 */
export function useTableColumns<TRow extends {}>(
  options: TableColumnsOptions<TRow>,
): UseTabeCols<TRow> & { reset: () => void } {
  const storageKey = toValue(options.storageKey)
  const columnsBase = toValue(options.columnsBase)
  const columnsBaseLeafs = getLeafsAll(columnsBase)
  const columnsDefault = copy(columnsBase)
  if (options.columnsDefault) {
    reduceTreeInplace(columnsDefault, options.columnsDefault)
  }

  /**
   * Primarily used to "instantiate" sortFn() and filterFn()
   */
  function mergeColValues(colRaw: ColumnItem<TRow>): ColumnItem<TRow> {
    if (isNestedBranch(colRaw)) {
      colRaw.children = colRaw.children.map(mergeColValues)
    }
    const colDefault = columnsBaseLeafs.find((col) => col.id === colRaw.id)
    if (!colDefault) {
      return colRaw
    }
    const colNew: ColumnItem<TRow> = {
      ...colDefault,
      ...colRaw,
    }
    return colNew
  }

  const sortKey = `${storageKey}.table-columns-sort`
  const columnsKey = `${storageKey}.table-columns`

  // Sometimes we always want to use fresh column settings (care, this can lead to bugs)
  if (options.purgeStorage) {
    localStorage.removeItem(sortKey)
    localStorage.removeItem(columnsKey)
  }

  // Create localStorage-backed refs
  const sort = useLocalStorage<DataTableSortItem[]>(sortKey, [])
  const columns = useLocalStorage<ColumnItem<TRow>[]>(columnsKey, columnsDefault, {
    serializer: {
      read(raw) {
        const columnsRaw = JSON.parse(raw) as ColumnItem<TRow>[]
        return columnsRaw.map(mergeColValues)
      },
      write(value) {
        return JSON.stringify(value)
      },
    },
  })

  /**
   * Reset columns and sort to default values
   */
  function reset() {
    columns.value = copy(columnsDefault)
    sort.value = []
  }

  // Delegate to base version
  const base = useTableColumnsBase<TRow>({
    columns,
    sort,
    columnsTitle: options.columnsTitle,
  })
  return {
    ...base,
    reset,
  }
}

/**
 * Convert (internal) ColumnItem to Vuetify's DataTableHeader
 *
 * See: https://vuetifyjs.com/en/components/data-tables/basics/#headers
 */
function columnToHeader<Row extends object>(
  col: ColumnItem<Row>,
  translate: (item: ColumnItem<Row>) => string,
): TableHeader<Row> {
  // Base object
  const { t } = useI18nGlobal()
  const header: TableHeader<Row> = {
    col,
    value: col.id,
    title: translate(col),
    tooltip: isNestedBranch(col) ? col.title : t(col.title, 1),
    align: col.align,
    headerProps: col.headerProps,
    nowrap: true,
  }

  if (isNestedBranch(col)) {
    // extend for parents

    // transform children
    header.children = col.children.map((child) => columnToHeader(child, translate))

    // default border for nested columns
    header.headerProps ??= {}
    header.headerProps = mergeProps(header.headerProps, { class: 'border-s' })
    if (col.children.length) {
      header.children[0].headerProps ??= {}
      header.children[0].headerProps = mergeProps(header.children[0].headerProps, { class: 'border-s' })
    }
  } else {
    // extend for leafs
    header.value = col.value as string // used by vuetify for slot name
    header.fixed = col.pinned
    header.sortRaw = col.sortFn
    header.filter = col.filterFn
    header.cellProps = col.cellProps
    header.headerProps ??= {}
    header.headerProps = mergeProps(header.headerProps, { class: ['border-s'] })

    // width only on leaf
    header.width = col.width
    header.maxWidth = col.width

    // static values
    header.sortable = true
  }
  return header
}

export function resizeColumnReset<T>(col: ColumnItem<T>) {
  if (!isNestedBranch(col)) {
    delete col.width
  }
}

export function resizeColumn<T>(e: MouseEvent, col: ColumnItem<T>) {
  const target = e.target instanceof HTMLElement ? e.target : null
  const th = target?.closest('th')
  const table = th?.closest('table')

  if (!target || !th || !table || isNestedBranch(col) || !(th.parentElement instanceof HTMLElement)) {
    return
  }

  // To get the correct <td>-List we need to find index
  // even when rowspan on table headers is used
  const map = computeColumnMap(table)
  const index = getThLogicalColumnIndex(th, map)
  const tds = Array.from(table!.querySelectorAll<HTMLElement>(`tbody td:nth-child(${index + 1})`))

  const controller = new AbortController()
  const rectTh = th.getBoundingClientRect()
  const initWidth = th.clientWidth
  const initX = rectTh.right
  const minWidth = 25
  let newWidth = initWidth

  /**
   * Render preview indicator (as a box overlaying the column)
   */
  const previewIndicator = target.cloneNode() as HTMLElement
  const rectTable = table.getBoundingClientRect()
  previewIndicator.style.position = 'fixed'
  previewIndicator.style.top = rectTh.top + 'px'
  previewIndicator.style.height = rectTable.bottom - rectTh.top + 'px'
  previewIndicator.style.left = rectTh.left + 'px'
  previewIndicator.style.width = initX - rectTh.left + 'px'
  previewIndicator.style.zIndex = '2'
  previewIndicator.style.backgroundColor = 'rgba(var(--v-theme-primary), 0.1)'
  previewIndicator.style.color = 'rgb(var(--v-theme-on-primary))'
  previewIndicator.innerText = previewIndicator.style.width
  document.body.appendChild(previewIndicator)

  /**
   * Both TH and TDs widths need to be aligned for the correct preview
   */
  function updateCells(width: number) {
    const widthPx = `${width}px`
    if (th) {
      th.style.width = widthPx
      th.style.maxWidth = widthPx
    }
    for (const td of tds) {
      td.style.width = widthPx
      td.style.maxWidth = widthPx
    }
  }

  /**
   * Updates width and previewIndicator
   */
  function resize(e: PointerEvent) {
    let width = initWidth + (e.x - initX)
    if (e.shiftKey) {
      width = roundToStep(width, 10)
    }

    // do not shrink below min
    if (width < minWidth) {
      width = minWidth
    }

    if (width === newWidth) {
      return
    }

    // previewIndicator.style.left = e.x + 'px'
    previewIndicator.style.left = `${(th as HTMLTableCellElement).getBoundingClientRect().left}px`
    previewIndicator.style.width = width + 'px'
    previewIndicator.innerText = previewIndicator.style.width
    newWidth = width
    updateCells(width)
  }

  /**
   * Cleanup
   */
  function done(e: PointerEvent | MouseEvent | KeyboardEvent) {
    previewIndicator.remove()
    updateCells(initWidth)
    controller.abort('DONE')
    if ('key' in e && e.key === 'Escape') {
      return
    }
    if (col && !isNestedBranch(col)) {
      col.width = newWidth
    }
  }
  document.addEventListener('pointermove', resize, { signal: controller.signal })
  document.addEventListener('pointerup', done, { signal: controller.signal })
  document.addEventListener('keydown', done, { signal: controller.signal })
}

/**
 * Respecting colspan and rowspan creating the each tr/th matrix
 */
function computeColumnMap(table: HTMLTableElement) {
  const map: HTMLTableCellElement[][] = []
  const rows = table.tHead?.rows || []

  for (let r = 0; r < rows.length; r++) {
    const cells = rows[r].cells
    if (!map[r]) map[r] = []
    let colPosition = 0

    // Finde das erste freie Feld in dieser header-Matrix-Zeile
    while (map[r][colPosition]) colPosition++

    for (const cell of cells) {
      // nächstes freies Feld finden, falls vorigen durch rowspan ausgefüllt
      while (map[r][colPosition]) colPosition++
      const rowSpan = cell.rowSpan || 1
      const colSpan = cell.colSpan || 1

      // Markiere die belegten Positionen
      for (let rr = 0; rr < rowSpan; rr++) {
        for (let cc = 0; cc < colSpan; cc++) {
          if (!map[r + rr]) map[r + rr] = []
          map[r + rr][colPosition + cc] = cell
        }
      }
      colPosition += colSpan
    }
  }
  return map
}

/**
 * Extract the col-index of a th
 */
function getThLogicalColumnIndex(th: HTMLTableCellElement, map: HTMLTableCellElement[][]) {
  for (let r = 0; r < map.length; r++) {
    for (let c = 0; c < map[r].length; c++) {
      if (map[r][c] === th) return c
    }
  }
  return -1
}
